import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, SvgIcon, Chip, IconButton, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { TreeSelect } from 'primereact/treeselect';
import { Box } from '@mui/system';
import ABSUtils from '@abs/utils/ABSUtils';
import absWebService from 'app/services/absWebService';
import clsx from 'clsx';

/**
 * Reusable ObjectItems TreeSelect Component
 * Used for selecting object items (projects, vehicles, etc.) with tree structure support
 *
 * @param {Object} props
 * @param {Array} props.objects - Array of object types from user.tl.objects
 * @param {Array} props.assignedProjects - Array of assigned projects from NewActivityDefaultsDataset.assignedprojects
 * @param {String} props.selectedObjectIDs - JSON string of selected object IDs
 * @param {Function} props.onChange - Callback when selection changes, receives JSON string of selected IDs
 * @param {Boolean} props.dense - Whether to use dense/compact styling (default: false)
 */
function ObjectItemsTreeSelect({ objects, assignedProjects, selectedObjectIDs, onChange, dense = false }) {
    const { t } = useTranslation('absadministration');
    const [localFavorites, setLocalFavorites] = useState({});

    // Check if an objectitem is currently favorited (local override or from backend data)
    const isFavorite = (objectitemId) => {
        if (localFavorites[objectitemId] !== undefined) return localFavorites[objectitemId];
        const item = assignedProjects?.find(p => p.Objectitem_ID == objectitemId);
        return item?.Is_Favorite == 1;
    };

    // Toggle favorite via API with optimistic update
    const handleToggleFavorite = async (objectitemId) => {
        const currentlyFav = isFavorite(objectitemId);
        // Optimistic update
        setLocalFavorites(prev => ({ ...prev, [objectitemId]: !currentlyFav }));

        try {
            const response = await absWebService.communicationHandler("tl_toggle_objectitem_favorite", { objectitemid: objectitemId });
            if (!absWebService.isResponseArrSuccess(response)) {
                // Rollback on error
                setLocalFavorites(prev => ({ ...prev, [objectitemId]: currentlyFav }));
            }
        } catch (err) {
            // Rollback on error
            setLocalFavorites(prev => ({ ...prev, [objectitemId]: currentlyFav }));
        }
    };

    // Convert JSON string to tree node item format
    const jsonToTreeNodeItem = (json) => {
        var values = {};
        try {
            JSON.parse(json).forEach(item => values[item] = true)
        }
        catch { }

        if (values == {})
            return null;
        else
            return values;
    }

    // Convert tree node item to JSON string
    const treeNodeItemToJSON = (treenodeitem) => {
        try {
            if (Object.keys(treenodeitem).length > 0)
                return JSON.stringify(Object.keys(treenodeitem));
            else
                return null;
        }
        catch { }
        return null;
    }

    // Build nested tree structure
    function objectItemsNested(data, pid = 0) {
        return data.reduce((r, e) => {
            if (pid == e.parent) {
                const object = { ...e }
                const children = objectItemsNested(data, e.key);

                if (children.length) {
                    object.children = children;
                    // If any child or descendant is a favorite, propagate upward
                    if (!object.isFavorite && children.some(c => c.isFavorite || c.hasFavoriteChild)) {
                        object.hasFavoriteChild = true;
                    }
                }

                r.push(object)
            }

            return r;
        }, []).sort((a, b) => {
            const aFav = (a.isFavorite || a.hasFavoriteChild) ? 1 : 0;
            const bFav = (b.isFavorite || b.hasFavoriteChild) ? 1 : 0;
            return bFav - aFav;
        })
    }

    // Convert object items to tree nodes
    const treeNodeConverter = (objecttype) => {
        var projects = assignedProjects ? Object.values(assignedProjects.filter(object => object.Object_key == objecttype))?.map((option, index) => {
            return { "key": option.Objectitem_ID, "label": option.Objectitem_Name, "color": option.Color, "svgicon": option.IconSVGContent, "parent": option.Sub_of ? option.Sub_of : 0, "isFavorite": isFavorite(option.Objectitem_ID) };
        }) : [];

        if (objects?.find(object => object.Object_key == objecttype)?.Sub_enabled == 1)
            return objectItemsNested(projects);
        else {
            // Sort favorites to top (flat list)
            projects.sort((a, b) => (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0));
            return projects;
        }
    }

    // Filter selected items for a specific object type
    const filterSelectedObjectItems = (object, exclude = false) => {
        var values = {};

        if (selectedObjectIDs) {
            if (exclude) {
                Object.keys(jsonToTreeNodeItem(selectedObjectIDs))?.filter(objectid => assignedProjects?.find(item => item.Objectitem_ID == objectid)?.Object_key != object.Object_key)?.forEach(item => values[item] = true);
            }
            else {
                Object.keys(jsonToTreeNodeItem(selectedObjectIDs))?.filter(objectid => assignedProjects?.find(item => item.Objectitem_ID == objectid)?.Object_key == object.Object_key)?.forEach(item => values[item] = true);
            }
        }

        if (values == {})
            return null;
        else
            return values;
    }

    // Change selected items (add or remove)
    const changeSelectedObjectItems = (object, objectitem, remove = false) => {
        var values = {};

        if (selectedObjectIDs) {
            if (remove) {
                Object.keys(jsonToTreeNodeItem(selectedObjectIDs))?.filter(objectid => objectid != objectitem.key)?.forEach(item => values[item] = true);
            }
        }

        if (!remove) {
            //if only single select, then remove all currently selected, else add all
            if (object.Max_select <= 1)
                Object.keys(jsonToTreeNodeItem(selectedObjectIDs))?.filter(objectid => assignedProjects?.find(item => item.Objectitem_ID == objectid)?.Object_key != object.Object_key)?.forEach(item => values[item] = true);
            else
                Object.keys(jsonToTreeNodeItem(selectedObjectIDs))?.forEach(item => values[item] = true);

            //only add when at least one
            if (object.Max_select >= 1)
                values[objectitem.key] = true
        }

        if (values == {})
            return null;
        else
            return values;
    }

    // Template for tree nodes
    const treeNodeTemplate = (node, options) => {
        let label = node.label;

        if (node.url) {
            label = <a href={node.url} className="text-primary hover:underline font-semibold">{node.label}</a>;
        }

        return (
            <>
                <Tooltip title={node.isFavorite ? t("REMOVEFAVORITE") : t("ADDFAVORITE")} PopperProps={{ style: { zIndex: 99999 } }}>
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleToggleFavorite(node.key);
                        }}
                        className="cursor-pointer mr-4 flex items-center"
                    >
                        {node.isFavorite ?
                            <StarIcon sx={{ fontSize: 18, color: '#f59e0b' }} /> :
                            <StarBorderIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                        }
                    </span>
                </Tooltip>
                {(node.color || node.svgicon) &&
                    <Box
                        sx={{
                            backgroundColor: (node.color) ? node.color : 'rgba(0,0,0,.12)',
                            color: (node.color) ? ABSUtils.invertColor(node.color, true) : 'rgba(0,0,0,.87)',
                        }}
                        className="flex items-center justify-center p-4 rounded-lg mr-8"
                    >
                        {node.svgicon ?
                            <SvgIcon sx={{ fontSize: 15 }}>
                                <svg dangerouslySetInnerHTML={{ __html: node.svgicon }} />
                            </SvgIcon>
                            :
                            <SvgIcon sx={{ fontSize: 12 }} hidden>
                            </SvgIcon>
                        }
                    </Box>
                }
                <span className={options.className}>{label}</span>
            </>
        );
    }

    // Template for selected values badge
    const treeValueTemplateBadge = (object) => {
        var items = filterSelectedObjectItems(object);

        return (
            <>
                {(items && Object.keys(items).length > 0) ?
                    <span className='flex flex-row justify-center'>
                        {Object.keys(items)?.map(item => {
                            var objectitem = assignedProjects?.find(obj => obj.Objectitem_ID == item);
                            return (
                                <Box
                                    key={item}
                                    sx={{
                                        backgroundColor: 'rgba(0,0,0,.12)',
                                        color: 'rgba(0,0,0,.87)',
                                    }}
                                    className="flex items-center justify-center p-4 rounded-lg mr-8">
                                    {objectitem?.IconSVGContent ?
                                        <Box
                                            sx={{
                                                backgroundColor: (objectitem?.Color) ? objectitem?.Color : 'rgba(0,0,0,0)',
                                                color: (objectitem?.Color) ? ABSUtils.invertColor(objectitem?.Color, true) : 'rgba(0,0,0,.87)',
                                            }}
                                            className="flex items-center justify-center p-4 rounded-lg mr-4"
                                        >
                                            <SvgIcon sx={{ fontSize: 15 }}>
                                                <svg dangerouslySetInnerHTML={{ __html: objectitem?.IconSVGContent }} />
                                            </SvgIcon>
                                        </Box>
                                        :
                                        objectitem?.Color ?
                                            <Box
                                                sx={{
                                                    backgroundColor: objectitem?.Color,
                                                    color: ABSUtils.invertColor(objectitem?.Color, true),
                                                }}
                                                className="flex items-center justify-center p-4 rounded-lg mr-8"
                                            >
                                                <SvgIcon sx={{ fontSize: 12 }} hidden>
                                                </SvgIcon>
                                            </Box>
                                            :
                                            <></>
                                    }
                                    <span className='flex self-center ml-2'>{objectitem?.Objectitem_Name}</span>
                                </Box>
                            )
                        }
                        )}
                    </span>
                    :
                    <span className='flex flex-row justify-center'>{t("NOOBJECTASSIGNMENT", { object: object?.Name })}</span>
                }
            </>
        );
    }

    return (
        <>
            {objects?.map(object => (
                treeNodeConverter(object.Object_key)?.length > 0 &&
                <div key={object.Object_key} className={dense ? "mb-16" : "mb-24"}>
                    <Typography variant="subtitle2" className={dense ? "mb-8 font-medium text-gray-600 text-12" : "mb-12 font-medium text-gray-600"}>
                        {t("DEFAULTOBJECTS", { objects: object.Name_plural })} {Object.values(filterSelectedObjectItems(object)).length > object.Max_select && <Chip label={t("SELECTIONEXCEEDSMAX")} color="error" size="small" className="ml-8" />}
                    </Typography>
                    <TreeSelect
                        inputId={"treeselect_" + object.Object_key}
                        value={filterSelectedObjectItems(object)}
                        valueTemplate={() => treeValueTemplateBadge(object)}
                        onNodeSelect={(e) => {
                            onChange(treeNodeItemToJSON(changeSelectedObjectItems(object, e.node)));
                        }}
                        onNodeUnselect={(e) => {
                            onChange(treeNodeItemToJSON(changeSelectedObjectItems(object, e.node, true)));
                        }}
                        onChange={(e) => {
                            //e.value
                            //nothing to do here since we manage select & unselect extra for better checking maxvalue
                        }}
                        options={treeNodeConverter(object.Object_key)}
                        filter
                        className={clsx(
                            "w-full text-13 h-full",
                            dense ? "py-2" : "py-6",
                            Object.values(filterSelectedObjectItems(object)).length > object.Max_select && 'p-invalid'
                        )}
                        nodeTemplate={treeNodeTemplate}
                        placeholder={t("NOOBJECTASSIGNMENT", { object: object.Name })}
                        emptyMessage={t("NOOBJECTSFOUND", { objects: object.Name_plural })}
                        resetFilterOnHide
                        selectionMode={object.Max_select > 1 ? "multiple" : "single"}
                        metaKeySelection={false}
                        display="chip"
                        pt={{
                            closeButton: {
                                onMouseUp: () => {
                                    onChange(treeNodeItemToJSON(filterSelectedObjectItems(object, true)));
                                }
                            },
                            filter: {
                                id: "treeselect_input_" + object.Object_key,
                                onFocus: (e) => {
                                    //unfocus search input on open for mobile keyboard hide
                                    if (e.relatedTarget?.id == "treeselect_" + object.Object_key) {
                                        document.getElementById("treeselect_input_" + object.Object_key)?.blur();
                                    }
                                }
                            }
                        }}
                    />
                </div>
            ))}
        </>
    );
}

export default ObjectItemsTreeSelect;

